
import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { AppModal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { AccountFormNested } from '@/components/forms/AccountFormNested';
import { useAccounts } from '@/contexts/accounts';

interface TransactionAccountFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  paymentMethod: string;
  transactionType: 'receita' | 'despesa';
  hideLabel?: boolean;
}

export const TransactionAccountField = ({
  value,
  onChange,
  error,
  disabled,
  paymentMethod,
  transactionType,
  hideLabel
}: TransactionAccountFieldProps) => {
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [lastCreatedAccountName, setLastCreatedAccountName] = useState<string | null>(null);
  const { accounts, addAccount } = useAccounts();

  // Debug para verificar o valor recebido
  console.log('TransactionAccountField - value:', value);
  console.log('TransactionAccountField - accounts:', accounts.map(acc => ({ id: acc.id, name: acc.name })));

  const selectedAccount = accounts.find(acc => acc.id === value);
  console.log('TransactionAccountField - selectedAccount:', selectedAccount);

  // Auto-select newly created account when accounts update
  useEffect(() => {
    if (lastCreatedAccountName && accounts.length > 0) {
      const newAccount = accounts.find(acc => acc.name === lastCreatedAccountName);
      if (newAccount && newAccount.id !== value) {
        console.log('Auto-selecting newly created account:', newAccount.name);
        onChange(newAccount.id);
        setLastCreatedAccountName(null);
      }
    }
  }, [accounts, lastCreatedAccountName, onChange, value]);

  const handleAddAccount = async (accountData: any) => {
    try {
      console.log('Creating account:', accountData.name);
      setLastCreatedAccountName(accountData.name);
      await addAccount(accountData);
      setShowAccountForm(false);
    } catch (error) {
      console.error('Error adding account:', error);
      setLastCreatedAccountName(null);
    }
  };

  const sortedAccounts = [...accounts].sort((a, b) => a.name.localeCompare(b.name));

  // Only show readonly field for CREDIT CARD expenses (not debit card)
  if (paymentMethod === 'cartao' && transactionType === 'despesa') {
    return (
      <div>
        {!hideLabel && <Label htmlFor="account">Conta</Label>}
        <Input
          id="account"
          value={value || 'Cartão selecionado'}
          readOnly
          disabled
          className="bg-muted border-muted-foreground/20"
          placeholder="Conta será definida pelo cartão selecionado"
        />
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  // For all other methods (including debit card), allow normal account selection
  return (
    <div>
      {!hideLabel && <Label htmlFor="accountId">Conta</Label>}
      <Select value={value} onValueChange={onChange} disabled={disabled} open={selectOpen} onOpenChange={setSelectOpen}>
        <SelectTrigger className={`rounded-xl ${error ? 'border-destructive' : ''}`}>
          <SelectValue placeholder="Selecionar conta">
            {selectedAccount && (
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: selectedAccount.color }} />
                <span>{selectedAccount.name} - {selectedAccount.bank}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {sortedAccounts.length === 0 ? (
            <div className="p-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-left" 
                onClick={() => {
                  setSelectOpen(false);
                  setShowAccountForm(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Nova Conta
              </Button>
            </div>
          ) : (
            <>
              {sortedAccounts.map(account => (
                <SelectItem key={account.id} value={account.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: account.color }} />
                    <span>{account.name} - {account.bank}</span>
                  </div>
                </SelectItem>
              ))}
              <div className="border-t mt-1 pt-1">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setSelectOpen(false);
                    setShowAccountForm(true);
                  }} 
                  className="w-full justify-start text-left text-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Nova Conta
                </Button>
              </div>
            </>
          )}
        </SelectContent>
      </Select>
      
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}

      <AppModal 
        open={showAccountForm} 
        onOpenChange={(open) => !open && setShowAccountForm(false)}
        variant="form"
        size="lg"
        isNested={true}
      >
        <AccountFormNested 
          onClose={() => setShowAccountForm(false)} 
          onSubmit={handleAddAccount} 
        />
      </AppModal>
    </div>
  );
};
